
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function CliftonPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Clifton Painters - Professional Painting Services Cape Town";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Clifton luxury beachfront properties. Expert coastal painting, marine-grade protection, and premium finishes. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Clifton painters, luxury beachfront painting, coastal property painting, marine grade coating, premium painting, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/clifton`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Clifton Painters - Professional Painting Services",
      "description": "Professional painting services for Clifton luxury beachfront properties. Expert coastal painting, marine-grade protection, and premium finishes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/clifton`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Clifton",
        "description": "Luxury coastal painting services for Clifton's exclusive beachfront properties.",
        "areaServed": "Clifton, Cape Town",
        "serviceType": "Luxury Coastal Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Clifton')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-smile-line',
      title: 'Luxury Beachfront Painting',
      description: 'Premium painting services for Clifton\'s exclusive beachfront properties with high-end finishes and marine-grade protection against coastal elements.',
      price: 'From R25,000',
      features: ['Premium materials', 'Marine-grade coatings', 'Luxury finishes', 'Coastal protection']
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Coastal Protection Systems',
      description: 'Advanced protective coatings specifically designed for Clifton\'s harsh coastal environment including salt air and ocean spray resistance.',
      price: 'From R18,000',
      features: ['Salt-resistant formulas', 'UV protection', 'Moisture barriers', 'Wind resistance']
    },
    {
      icon: 'ri-palette-line',
      title: 'Designer Color Consultation',
      description: 'Expert color consultation services for luxury Clifton properties with sophisticated palettes that complement the coastal setting.',
      price: 'From R3,500',
      features: ['Luxury color schemes', 'Coastal aesthetics', 'Designer trends', 'Custom matching']
    },
    {
      icon: 'ri-building-4-line',
      title: 'Apartment Complex Painting',
      description: 'Comprehensive painting services for Clifton\'s luxury apartment buildings and residential complexes with premium standards.',
      price: 'From R35,000',
      features: ['Complex coordination', 'Minimal disruption', 'Premium standards', 'Scheduled maintenance']
    },
    {
      icon: 'ri-brush-3-line',
      title: 'Interior Luxury Finishes',
      description: 'High-end interior painting with decorative finishes, textures, and specialty coatings for discerning Clifton homeowners.',
      price: 'From R12,000',
      features: ['Decorative techniques', 'Luxury textures', 'Custom finishes', 'Designer coordination']
    },
    {
      icon: 'ri-tools-fill',
      title: 'Maintenance Programs',
      description: 'Exclusive maintenance programs for Clifton properties including regular inspections, touch-ups, and protective treatments.',
      price: 'From R8,000',
      features: ['Regular inspections', 'Preventive maintenance', 'Emergency service', 'Property protection']
    }
  ];

  const testimonials = [
    {
      name: 'Alexander Sterling',
      property: 'Clifton Penthouse',
      rating: 5,
      text: 'Exceptional quality and attention to detail for our beachfront penthouse. The marine-grade coatings have performed beautifully against the harsh coastal conditions.',
      image: 'https://public.readdy.ai/ai/img_res/3e580ccb9ac3d989f427c2bc9fd5e84e.jpg'
    },
    {
      name: 'Victoria Hamilton',
      property: 'Clifton Beach House',
      rating: 5,
      text: 'Outstanding service for our luxury beach house. The team understood the unique challenges of coastal painting and delivered results that exceed our expectations.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20in%20luxury%20Clifton%20beach%20house%20with%20ocean%20views%2C%20sophisticated%20decor%2C%20professionally%20painted%20interior%2C%20satisfied%20client&width=80&height=80&seq=clifton2&orientation=squarish'
    },
    {
      name: 'Robert Blackwood',
      property: 'Clifton Villa',
      rating: 5,
      text: 'Premium quality work that matches the prestige of Clifton. The color consultation was invaluable and the finish quality is absolutely flawless throughout.',
      image: 'https://readdy.ai/api/search-image?query=Distinguished%20man%20in%20luxury%20Clifton%20villa%20with%20Atlantic%20Ocean%20backdrop%2C%20premium%20interior%20design%2C%20professionally%20painted%20walls%2C%20satisfied%20homeowner&width=80&height=80&seq=clifton3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Luxury Penthouse Renovation',
      description: 'Complete interior and exterior painting of exclusive Clifton penthouse with premium finishes and coastal protection systems',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20penthouse%20in%20Clifton%20with%20Atlantic%20Ocean%20views%2C%20premium%20painted%20exterior%2C%20modern%20architecture%2C%20exclusive%20beachfront%20property&width=400&height=300&seq=clifton4&orientation=landscape'
    },
    {
      title: 'Beachfront Villa Complex',
      description: 'Comprehensive painting project for luxury villa complex including marine-grade coatings and sophisticated color schemes',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20villa%20complex%20in%20Clifton%20with%20Atlantic%20Ocean%20setting%2C%20professionally%20painted%20exteriors%2C%20exclusive%20coastal%20properties&width=400&height=300&seq=clifton5&orientation=landscape'
    },
    {
      title: 'Modern Beach House',
      description: 'Contemporary painting solutions for modern Clifton beach house with cutting-edge materials and designer finishes',
      image: 'https://readdy.ai/api/search-image?query=Modern%20beach%20house%20in%20Clifton%20with%20contemporary%20architecture%2C%20professionally%20painted%20exterior%2C%20Atlantic%20Ocean%20views%2C%20luxury%20coastal%20home&width=400&height=300&seq=clifton6&orientation=landscape'
    },
    {
      title: 'Heritage Clifton Property',
      description: 'Restoration and protection of historic Clifton property with period-appropriate techniques and modern coastal protection',
      image: 'https://readdy.ai/api/search-image?query=Historic%20property%20in%20Clifton%20with%20restored%20painted%20exterior%2C%20heritage%20architecture%2C%20Atlantic%20Ocean%20setting%2C%20professional%20restoration%20work&width=400&height=300&seq=clifton7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20beachfront%20property%20in%20Clifton%20with%20Atlantic%20Ocean%20backdrop%2C%20exclusive%20coastal%20estate%2C%20painting%20equipment%2C%20scaffolding&width=1920&height=1080&seq=cliftonhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Clifton
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Clifton's luxury beachfront properties. Specialized coastal protection and premium finishes for your exclusive home with marine-grade materials designed for the ultimate coastal lifestyle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Clifton Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in Clifton
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Clifton stands as Cape Town's most prestigious beachfront destination, home to some of the world's most beautiful beaches and luxury properties. Our specialized painting services cater to the unique demands of this exclusive coastal location, where properties face constant exposure to salt air, ocean spray, and intense UV radiation.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located along the Atlantic Seaboard, Clifton's luxury homes and apartments require more than standard painting solutions. We provide comprehensive services using premium marine-grade materials specifically formulated to withstand the harsh coastal environment while maintaining the sophisticated aesthetic expected in this world-renowned location.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with Clifton's diverse architectural styles, from contemporary glass structures to classic coastal designs, ensures we deliver results that enhance your property's value while providing superior protection against the challenging coastal elements.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Clifton%20beachfront%20with%20luxury%20properties%2C%20Atlantic%20Ocean%20views%2C%20exclusive%20coastal%20homes%2C%20pristine%20beaches%2C%20Cape%20Town%20Atlantic%20Seaboard&width=600&height=400&seq=clifton8&orientation=landscape"
                alt="Clifton Beachfront Properties"
                className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Clifton Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized luxury painting solutions designed for Clifton's exclusive beachfront properties and coastal environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                        <i className={`${feature.icon} text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Clifton Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in luxury coastal painting with recent successful projects in Clifton.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Clifton Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Clifton property owners who have experienced our exceptional luxury painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Clifton Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's premier luxury painting specialists for Clifton. Our expert team is ready to provide you with premium coastal painting solutions that match the prestige of your beachfront property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
